/* eslint-disable */
// @ts-nocheck

import { LightningBoltOutline } from 'heroicons-react'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  deleteContainerBatch,
  deleteContainerProducts,
  getContainerBatches,
  getContainerProducts,
  patchContainerBatch,
  patchContainerProducts,
  postContainerBatches,
  postContainerProducts,
} from '@/api/containersAPI'
import { getCurrencies } from '@/api/currenciesAPI'
import { getProducts, postProducts } from '@/api/productsAPI'
import { getProviders, getSuppliers } from '@/api/providersAPI'
import Button from '@/components/misc/Button'
import ButtonLoading from '@/components/misc/Button'
import SpinnerLoading from '@/components/misc/SpinnerLoading'
import {
  ButtonPrimary,
  DataTable,
  Modal,
  SelectForm,
  TableContextProvider,
  TextAreaForm,
} from '@/components/new'
import { ButtonIcon } from '@/components/new/buttons/ButtonIcon'
import { InputCurrencyForm } from '@/components/new/form/InputCurrencyForm'
import { InputForm } from '@/components/new/form/InputForm'
import { useModalWizardStepsContext } from '@/components/new/modalWizardSteps/ModalWizardStepsContext'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { Button as ButtonCancelar } from '@/components/ui/button'
import { DialogFooter } from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { IProd } from '@/containers/logistic/expedients/wizzardExpedient/tabs/cargo/CargoExpTab'
import useContainerWizzardSteps from '@/state/useContainerWizzardSteps'
import { useAppSelector } from '@/store/hooks'
import { productTypes } from '@/utils/staticData'

import { DateFilter } from '../../../../../billing/register/billingFilters/filter/DateFilter'
import { IContainer, IProduct } from '../../../interfaces/interfaces'
import { columns } from './columns'
import { SelectFormProducts } from './SelectFormProducts'

// Tipos de productos permitidos para contenedores
const containerProductTypes = productTypes.filter(type => 
  type.id && ['STOCK', 'MANUFACTURED', 'INPUT', 'ASSET', 'RAW', 'VARIATION'].includes(type.id as string)
)

export default function ProductsContainerTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()
  const form = context.useFormWizzard
  const { control, getValues, setValue } = form
  const [showProductContainerModal, setShowProductContainerModal] =
    useState(false)
  const [showNewProductModal, setShowNewProductModal] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const [isProductEditing, setIsProductEditing] = useState(false)
  const [productSelected, setProductSelected] = useState<string>()
  const [productToDelete, setProductToDelete] = useState<number>()
  const [isCostEditing, setIsCostEditing] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<any>(null)
  const [providerSelected, setProviderSelected] = useState<any>(null)
  const [showDeleteProduct, setShowDeleteProduct] = useState(false)
  const [showRepetedModal, setShowRepetedModal] = useState(false)
  const [showEmptyModal, setShowEmptyModal] = useState(false)
  const [showBatchModal, setShowBatchModal] = useState(false)
  const [isBatchEditing, setIsBatchEditing] = useState(false)
  const [selectedProductForBatch, setSelectedProductForBatch] =
    useState<any>(null)
  const [batchToEdit, setBatchToEdit] = useState<any>(null)
  const [batchToDelete, setBatchToDelete] = useState<number>()
  const [showDeleteBatch, setShowDeleteBatch] = useState(false)
  const isFormResetRef = useRef(false)

  const [dataTableNewContainer, setDataTableNewContainer] = useState<IProd[]>(
    []
  )
  const data = useContainerWizzardSteps((state) => state.data)
  const updateData = useContainerWizzardSteps((state) => state.updateData)
  // 'products' no existe en DataType, así que inicializamos como array vacío si no existe
  const [productTable, setProductTable] = useState<IProduct[]>([])
  const [isEnrichingData, setIsEnrichingData] = useState(false)

  useEffect(() => {
    if (form) {
      const selectedProduct = form.watch('products')

      if (
        selectedProduct &&
        Array.isArray(selectedProduct) &&
        selectedProduct.length > 1
      ) {
        const productId = selectedProduct[1]
        form.setValue('productId', productId)
      } else if (
        selectedProduct &&
        typeof selectedProduct === 'object' &&
        selectedProduct.id
      ) {
        form.setValue('productId', selectedProduct.id)
      } else if (
        selectedProduct &&
        (typeof selectedProduct === 'string' ||
          typeof selectedProduct === 'number')
      ) {
        form.setValue('productId', selectedProduct)
      } else {
        form.setValue('productId', null)
      }
    }
  }, [form?.watch('products'), form])

  const { data: providers } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () =>
      getSuppliers({
        page: 1,
        per_page: 100,
        search: '',
      }),
    staleTime: 5 * 60 * 1000,
  })

  const { data: containerProducts, isLoading: isProductLoading } = useQuery({
    queryKey: ['container_products'],
    queryFn: () =>
      getProducts({
        page: 1,
        per_page: 10,
        search: '',
        type: 'STOCK,RAW,MANUFACTURED,INPUT,ASSET,VARIATION',
      }),
    staleTime: 5 * 60 * 1000,
  })

  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
  })

  // Set form values when editing a batch
  useEffect(() => {
    if (isBatchEditing && batchToEdit && showBatchModal) {
      // Set the form values with batch data
      form.setValue(
        'entryQuantity',
        batchToEdit.entryQuantity?.toString() ||
          batchToEdit.quantity?.toString() ||
          ''
      )
      form.setValue('expirationAt', batchToEdit.expirationAt || '')
      form.setValue('noPackages', batchToEdit.noPackages?.toString() || '')
      form.setValue('supplierId', batchToEdit.supplierId?.toString() || '')

      // Set registeredPrice values ensuring they are properly formatted
      if (batchToEdit.registeredPrice || batchToEdit.price) {
        form.setValue('registeredPrice', {
          amount:
            batchToEdit.registeredPrice?.amount ||
            batchToEdit.price?.amount ||
            0,
          codeCurrency:
            batchToEdit.registeredPrice?.codeCurrency ||
            batchToEdit.price?.codeCurrency ||
            'USD',
        })
      } else {
        form.setValue('registeredPrice', {
          amount: 0,
          codeCurrency: 'USD',
        })
      }
    }
  }, [isBatchEditing, batchToEdit, showBatchModal, form])

  // Reset form when opening modal for new batch (only once)
  useEffect(() => {
    if (
      !isBatchEditing &&
      showBatchModal &&
      !batchToEdit &&
      !isFormResetRef.current
    ) {
      // Reset form when adding new batch - only when modal opens for the first time
      form.reset({
        entryQuantity: '',
        expirationAt: '',
        noPackages: '',
        supplierId: '',
        registeredPrice: {
          amount: 0,
          codeCurrency:
            currencies && currencies.length > 0 ? currencies[0].code : 'USD',
        },
      })
      isFormResetRef.current = true
    } else if (!showBatchModal) {
      // Reset the flag when modal is closed
      isFormResetRef.current = false
    }
  }, [showBatchModal, isBatchEditing, batchToEdit, form, currencies])

  // Query to get container batches when editing
  const { data: containerBatches, isLoading: isBatchesLoading } = useQuery({
    queryKey: ['container_batches', editContainer?.id],
    queryFn: () => getContainerBatches(editContainer?.id as number),
    enabled: !!editContainer?.id && isEditing,
    staleTime: 5 * 60 * 1000,
  })

  // Update productTable when containerBatches are loaded
  useEffect(() => {
    const enrichBatchesWithProductData = async () => {
      if (containerBatches && isEditing && Array.isArray(containerBatches)) {
        setIsEnrichingData(true)

        // Clear productTable first to avoid showing empty columns
        setProductTable([])

        try {
          // Check if there are any batches/products
          if (containerBatches.length === 0) {
            // No data available, set empty array and stop enriching
            setProductTable([])
            setIsEnrichingData(false)
            return
          }

          // Use batch data directly since it already contains all needed information
          // entryQuantity, availableQuantity, grossCost, netCost, registeredPrice
          const processedBatches = containerBatches.map((batch) => {
            return {
              ...batch, // Keep all original batch data which includes all costs and quantities
              // Use a simple name format since we have the productId
              name: batch.name || `Producto ${batch.productId}`,
            }
          })

          setProductTable(processedBatches)
        } catch (error) {
          // Fallback to original batches if processing fails
          setProductTable(containerBatches)
        } finally {
          setIsEnrichingData(false)
        }
      } else if (
        isEditing &&
        containerBatches &&
        containerBatches.length === 0
      ) {
        // Explicitly handle empty case for editing mode
        setProductTable([])
        setIsEnrichingData(false)
      }
    }

    enrichBatchesWithProductData()
  }, [containerBatches, isEditing])

  const queryClient = useQueryClient()

  const postNewProduct = useMutation({
    mutationFn: postProducts,
    onSuccess: (response) => {
      setIsSending(false)
      setShowNewProductModal(false)

      // Después de crear el producto, configurar para abrir el modal de lote
      // usando el mismo modal que se usa para productos existentes
      const newProduct = {
        id: response.id || response.data?.id,
        title: response.name || response.data?.name,
        subtitle: response.subtitle || response.data?.subtitle,
      }

      setSelectedProductForBatch(newProduct)

      // Usar setTimeout para asegurar que el estado se actualice antes de abrir el modal
      setTimeout(() => {
        setShowBatchModal(true)
      }, 100)

      toast.success('El producto se ha creado con éxito')
      queryClient.invalidateQueries({ queryKey: ['products'] })
      form.reset()
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const postContainerBatchesMutation = useMutation({
    mutationFn: postContainerBatches,
    onSuccess: () => {
      setIsSending(false)
      setShowBatchModal(false)
      setSelectedProductForBatch(null) // Clear the stored product selection
      toast.success('El lote de producto se ha agregado con éxito')
      queryClient.invalidateQueries({
        queryKey: ['container_batches', editContainer?.id],
      })
      queryClient.invalidateQueries({ queryKey: ['container_products'] })
      form.reset()
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const patchContainerBatchMutation = useMutation({
    mutationFn: patchContainerBatch,
    onSuccess: () => {
      setIsSending(false)
      setShowBatchModal(false)
      setIsBatchEditing(false)
      setBatchToEdit(null)
      setSelectedProductForBatch(null) // Clear the stored product selection
      toast.success('El lote de producto se ha actualizado con éxito')
      queryClient.invalidateQueries({
        queryKey: ['container_batches', editContainer?.id],
      })
      queryClient.invalidateQueries({ queryKey: ['container_products'] })
      form.reset()
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const deleteContainerBatchMutation = useMutation({
    mutationFn: deleteContainerBatch,
    onSuccess: () => {
      setShowDeleteBatch(false)
      setBatchToDelete(undefined)
      toast.success('El lote se ha eliminado con éxito')
      queryClient.invalidateQueries({
        queryKey: ['container_batches', editContainer?.id],
      })
      queryClient.invalidateQueries({ queryKey: ['container_products'] })
    },
    onError: (response: any) => {
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  // Container products mutations
  const postContainerProductsMutation = useMutation({
    mutationFn: postContainerProducts,
    onSuccess: () => {
      setIsSending(false)
      setShowProductContainerModal(false)
      toast.success('El producto del contenedor se ha creado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_products'] })

      form.reset()
      setIsProductEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const updateContainerProductsMutation = useMutation({
    mutationFn: patchContainerProducts,
    onSuccess: () => {
      setIsSending(false)
      setShowProductContainerModal(false)
      toast.success('El producto del contenedor se ha actualizado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_products'] })

      form.reset()
      setIsProductEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const deleteContainerProductsMutation = useMutation({
    mutationFn: deleteContainerProducts,
    onSuccess: () => {
      setIsSending(false)
      toast.success('El producto del contenedor se ha eliminado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_products'] })

      form.reset()
      setIsProductEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  useEffect(() => {
    // Initialize products field to null to indicate no product selected
    // Only do this once on component mount
    setValue('products', null)
  }, [])

  // Exposed function for the wizard save button
  useEffect(() => {
    const handleSaveProducts = () => {
      // Close the container modal when save is clicked
      // @ts-ignore
      if (window.closeContainerModal) {
        // @ts-ignore
        window.closeContainerModal()
      }
    }

    // @ts-ignore
    window.handleSaveProducts = handleSaveProducts
    // @ts-ignore
    window.isSavingProducts = false

    return () => {
      // @ts-ignore
      delete window.handleSaveProducts
      // @ts-ignore
      delete window.isSavingProducts
    }
  }, [])

  // Removed useEffect for costTable as it's not defined

  // Removed useEffect for containerCosts as it's not defined

  const { business } = useAppSelector((state) => state.init)

  const saveBatch = (data: any) => {
    setIsSending(true)

    // Validate and parse registeredPrice amount - handle nested structure
    let priceAmount = data.registeredPrice?.amount

    // Handle nested amount structure
    if (typeof priceAmount === 'object' && priceAmount?.amount !== undefined) {
      priceAmount = priceAmount.amount
    }

    let parsedAmount = 0
    if (typeof priceAmount === 'string') {
      // Remove any non-numeric characters except dots and commas
      const cleanedAmount = priceAmount
        .replace(/[^\d.,]/g, '')
        .replace(',', '.')
      parsedAmount = parseFloat(cleanedAmount)
    } else if (typeof priceAmount === 'number') {
      parsedAmount = priceAmount
    } else {
      parsedAmount = 0
    }

    if (isNaN(parsedAmount) || parsedAmount < 0) {
      toast.error(`Precio inválido: ${priceAmount} -> ${parsedAmount}`)
      setIsSending(false)
      return
    }

    // Parse entryQuantity
    const entryQuantity = parseInt(data.entryQuantity, 10)
    if (isNaN(entryQuantity) || entryQuantity <= 0) {
      toast.error('La cantidad de entrada debe ser un número positivo')
      setIsSending(false)
      return
    }

    // Parse noPackages (optional)
    let noPackages = null
    if (data.noPackages) {
      const parsedPackages = parseInt(data.noPackages, 10)
      if (!isNaN(parsedPackages) && parsedPackages > 0) {
        noPackages = parsedPackages
      }
    }

    // Parse supplierId (optional)
    let supplierId = null
    if (data.supplierId) {
      const parsedSupplierId = parseInt(data.supplierId, 10)
      if (!isNaN(parsedSupplierId) && parsedSupplierId > 0) {
        supplierId = parsedSupplierId
      }
    }

    // Prepare the batch parameters
    const batchParams: any = {
      expirationAt: data.expirationAt || null,
      entryQuantity: entryQuantity,
      registeredPrice: {
        amount: Number(parsedAmount.toFixed(2)), // Ensure it's a clean number with 2 decimals max
        codeCurrency:
          data.registeredPrice?.codeCurrency ||
          (currencies && currencies.length > 0 ? currencies[0].code : 'USD'),
      },
    }

    // Add optional fields if they have values
    if (noPackages !== null) {
      batchParams.noPackages = noPackages
    }

    if (supplierId !== null) {
      batchParams.supplierId = supplierId
    }

    if (isBatchEditing && batchToEdit) {
      // Editing existing batch
      patchContainerBatchMutation.mutate({
        idBatch: batchToEdit.id,
        params: batchParams,
      })
      return
    }

    // Creating new batch
    // Use the stored selected product instead of form value
    // because form value gets reset when modal opens
    const selectedProduct = selectedProductForBatch

    let selectedProductId = null

    // Handle different possible formats of selectedProduct
    if (Array.isArray(selectedProduct)) {
      // Format: [title, id] - get the id from position 1
      selectedProductId = selectedProduct.length > 1 ? selectedProduct[1] : null
    } else if (
      selectedProduct &&
      typeof selectedProduct === 'object' &&
      selectedProduct.id
    ) {
      // Format: {id, title, subtitle} - get the id property
      selectedProductId = selectedProduct.id
    } else if (
      selectedProduct &&
      (typeof selectedProduct === 'string' ||
        typeof selectedProduct === 'number')
    ) {
      // Format: direct id value
      selectedProductId = selectedProduct
    }

    if (!selectedProductId) {
      toast.error('Debe seleccionar un producto para agregar un lote.')
      setIsSending(false)
      return
    }

    // Ensure productId is a positive integer
    const productId =
      typeof selectedProductId === 'number'
        ? selectedProductId
        : parseInt(selectedProductId, 10)

    if (!productId || productId <= 0 || isNaN(productId)) {
      toast.error(
        `ID de producto inválido: ${selectedProductId} -> ${productId}`
      )
      setIsSending(false)
      return
    }

    const payload = {
      id: editContainer?.id as number,
      batches: [
        {
          productId: productId,
          ...batchParams,
        },
      ],
    }

    postContainerBatchesMutation.mutate(payload)
  }

  const saveNewProduct = (data: any) => {
    setIsSending(true)
    //setValue('products', [data.product_name, undefined])
    setProductSelected(data.product_name)
    postNewProduct.mutate({
      name: data.product_name,
      type: data.product_type,
      prices: [
        {
          price: data.product_price?.amount ?? 0,
          codeCurrency: data.product_price?.codeCurrency ?? '',
        },
      ],
    })
  }

  const saveProductContainer = (product: any) => {
    const productData = {
      productId: product?.product?.id,
      noPackages: Number(product?.product_noPackages),
      description: product?.description,
      uniqueCode: product?.product_uniqueCode,
      provider: product?.product_provider,
      cost: product?.product_cost,
      availableQuantity: product?.availableQuantity,
      expirationAt: product?.expirationAt,
    }
    const id = product?.id

    if (isEditing) {
      if (isProductEditing) {
        setIsSending(true)
        updateContainerProductsMutation.mutate({
          id,
          params: productData,
        })
      } else {
        setIsSending(true)
        postContainerBatchesMutation.mutate({
          id: editContainer?.id as number,
          batches: [
            {
              productId: getValues('products')[1],
              expirationAt: product.product_expirationAt,
              entryQuantity: product.product_quantity,
              noPackages: product.product_noPackages || undefined,
              registeredPrice: {
                amount: Number(product.product_cost.amount),
                codeCurrency: product.product_cost.codeCurrency,
              },
            },
          ],
        })
      }
    } else {
      // When adding a new product to a new container (not yet saved to backend)
      // or adding a new product to an existing container (not in edit product mode)
      setIsSending(true)
      postContainerBatchesMutation.mutate({
        id: editContainer?.id as number, // Use editContainer.id if available, otherwise handle new container case
        batches: [
          {
            productId: product.product.id,
            expirationAt: product.expirationAt,
            entryQuantity: product.availableQuantity,
            noPackages: product.noPackages || undefined,
            registeredPrice: {
              amount: Number(product.product_cost.amount),
              codeCurrency: product.product_cost.codeCurrency,
            },
          },
        ],
      })
    }
  }

  return (
    <div className={'h-[60dvh]'}>
      {isProductLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <SpinnerLoading />
        </div>
      ) : (
        <>
          <div className={'flex items-end space-x-2'}>
            <div className="w-full">
              <SelectFormProducts
                control={control}
                data={
                  Array.isArray(containerProducts)
                    ? containerProducts.map(
                        ({ id, name, salesCode, description }) => ({
                          id: id,
                          title: salesCode ? `${name}-${salesCode}` : name,
                          subtitle: description,
                        })
                      )
                    : []
                }
                label={'Listado de productos'}
                name={'products'}
              />
            </div>
            <div className={'flex items-center space-x-2'}>
              <ButtonIcon
                onClick={() => {
                  const currentProducts = form.watch('products')
                  
                  // Verificar que se haya seleccionado un producto
                  if (!currentProducts || (Array.isArray(currentProducts) && currentProducts.length === 0)) {
                    toast.error('Debe seleccionar un producto antes de agregar un lote.')
                    return
                  }

                  // Store the selected product for the batch modal
                  setSelectedProductForBatch(currentProducts)

                  setShowBatchModal(true)
                  setIsBatchEditing(false)
                  setBatchToEdit(null)
                }}
              >
                <PlusIcon className="h-5" color={'white'} strokeWidth={3} />
              </ButtonIcon>
              <ButtonIcon onClick={() => setShowNewProductModal(true)}>
                <LightningBoltOutline
                  className="h-5"
                  color={'white'}
                  onPointerEnterCapture={() => {}}
                  onPointerLeaveCapture={() => {}}
                  strokeWidth={2}
                />
              </ButtonIcon>
              {/*<ButtonIcon onClick={() => {}}>
                <DownloadOutline
                  className="h-5"
                  color={'white'}
                  strokeWidth={3}
                />
              </ButtonIcon> */}
            </div>
          </div>

          {/* Show loading state when fetching batches */}
          {isEditing && (isBatchesLoading || isEnrichingData) ? (
            <div className="flex h-96 w-full items-center justify-center">
              <SpinnerLoading />
            </div>
          ) : (
            <TableContextProvider data={productTable} isLoading={false}>
              <DataTable
                columns={[
                  ...columns,
                  {
                    accessorKey: 'actions',
                    header: 'Acciones',
                    cell: ({ row }) => {
                      const original = row.original
                      // For batches, always show actions. For products, check actions.edit
                      const shouldShowActions =
                        isEditing || original?.actions?.edit

                      return (
                        <div className={'flex justify-center'}>
                          {shouldShowActions && (
                            <div className={'flex'}>
                              <div
                                className={`p-2`}
                                onClick={() => {
                                  // In editing mode, all items should be treated as batches
                                  if (isEditing || original.productId) {
                                    // This is a batch - open batch modal for editing
                                    setShowBatchModal(true)
                                    setIsBatchEditing(true)
                                    setBatchToEdit(original)
                                    
                                    // Set the product for the batch
                                    setSelectedProductForBatch(original.product || { 
                                      id: original.productId, 
                                      name: original.product?.name || original.name || 'Producto' 
                                    })
                                    
                                    form.setValue(
                                      'entryQuantity',
                                      original.totalQuantity ||
                                        original.entryQuantity ||
                                        0
                                    )
                                    form.setValue(
                                      'expirationAt',
                                      original.expirationAt
                                        ? new Date(original.expirationAt)
                                        : null
                                    )
                                    form.setValue(
                                      'noPackages',
                                      original.noPackages || null
                                    )
                                    form.setValue(
                                      'supplierId',
                                      original.supplierId || null
                                    )
                                    form.setValue('registeredPrice', {
                                      amount:
                                        original.averageCost ||
                                        original.registeredPrice?.amount ||
                                        0,
                                      codeCurrency:
                                        original.registeredPrice
                                          ?.codeCurrency || 'USD',
                                    })
                                  } else {
                                    // This is a regular product - open batch modal (mismo modal para consistencia)
                                    setShowBatchModal(true)
                                    setIsProductEditing(true)
                                    form.setValue('id', original.id)
                                    form.setValue(
                                      'entryQuantity',
                                      original.quantity ||
                                        original.availableQuantity ||
                                        0
                                    )
                                    setProductSelected(
                                      original.product?.name ||
                                        original.name ||
                                        ''
                                    )
                                    form.setValue(
                                      'noPackages',
                                      original.noPackages || 0
                                    )
                                    form.setValue(
                                      'description',
                                      original.description || ''
                                    )
                                    form.setValue(
                                      'uniqueCode',
                                      original.uniqueCode || ''
                                    )
                                    form.setValue(
                                      'provider',
                                      original.product_provider || null
                                    )
                                    form.setValue(
                                      'grossCost',
                                      original.product_cost ||
                                        original.price ||
                                        null
                                    )
                                    form.setValue(
                                      'netCost',
                                      original.product_cost ||
                                        original.price ||
                                        null
                                    )
                                    form.setValue(
                                      'expirationAt',
                                      original.expirationAt
                                        ? new Date(original.expirationAt)
                                        : null
                                    )
                                    setProviderSelected(
                                      original.product_provider || null
                                    )
                                  }
                                }}
                                role={'button'}
                              >
                                <PencilSquareIcon
                                  className={`h-5 w-5 text-blue-700`}
                                />
                              </div>
                              <div
                                className={`p-2`}
                                onClick={() => {
                                  // Check if this is a batch (has productId) or a regular product
                                  if (original.productId) {
                                    // This is a batch - show delete batch modal
                                    setShowDeleteBatch(true)
                                    setBatchToDelete(original.id)
                                  } else {
                                    // This is a regular product - show delete product modal
                                    setShowDeleteProduct(true)
                                    setProductToDelete(original.id)
                                  }
                                }}
                                role={'button'}
                              >
                                <TrashIcon className={`h-5 w-5 text-red-700`} />
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    },
                  },
                ]}
                storageKey={'ProductsContainerTab'}
              />
            </TableContextProvider>
          )}
        </>
      )}

      {/* COMENTADO: Modal de producto contenedor - ahora se usa showBatchModal para ambos casos
      {showProductContainerModal && (
        <Modal
          isOpen={showProductContainerModal}
          onClose={() => {
            setIsCostEditing(false)
            setShowProductContainerModal(false)
            setSelectedCategory(null)
            form.reset()
          }}
          showDialogAlert={false}
          size={'xs'}
          title={
            productSelected ??
            (getValues('products') && getValues('products')[0])
          }
        >
          <div
            className={
              'flex w-full flex-col overflow-y-scroll px-2 scrollbar-thin'
            }
          >
            <Form {...form}>
              <div className={'flex w-full flex-col space-y-2'}>
                <div className="flex w-full gap-2">
                  <div className="flex-1">
                    <InputForm
                      control={form.control}
                      label={'Cantidad'}
                      name={'product_quantity'}
                      placeholder={'Cantidad'}
                      rules={{
                        required: 'Campo requerido',
                      }}
                      type="number"
                    />
                  </div>
                </div>
                <div className="flex w-full gap-2">
                  <div className="flex-1">
                    <DateFilter
                      buttonClassName="w-full"
                      dateFrom={form.watch('product_expirationAt')}
                      dateTo={null}
                      hideDateTypeSelect
                      onDateRangeChange={(dateFrom) =>
                        form.setValue('product_expirationAt', dateFrom)
                      }
                      onDateTypeChange={() => {}}
                      onlySpecific
                      selectedDateType="specific"
                    />
                  </div>
                  <div className="flex-1">
                    <InputForm
                      control={form.control}
                      label={'Número de paquetes'}
                      name={'product_noPackages'}
                      placeholder={'Número de paquetes'}
                      type="number"
                    />
                  </div>
                </div>

                <div className="flex w-full gap-2">
                  <div className="flex-1">
                    {currencies && (
                      <InputCurrencyForm
                        control={form.control}
                        currenciesData={
                          Array.isArray(currencies)
                            ? currencies.map(({ code }) => ({
                                code: code,
                                displayName: code,
                              }))
                            : []
                        }
                        label={'Costo (*)'}
                        name={'product_cost'}
                        rules={{
                          required: 'Campo requerido',
                        }}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <SelectForm
                      control={form.control}
                      data={
                        Array.isArray(providers)
                          ? providers.map(({ id, name }) => ({
                              id,
                              title: name,
                              subtitle: '',
                            }))
                          : []
                      }
                      defaultValue={providerSelected}
                      label={'Proveedor (*)'}
                      name={'product_provider'}
                      rules={{
                        required: 'Campo requerido',
                      }}
                    />
                  </div>
                </div>
                <TextAreaForm
                  control={form.control}
                  label={'Observaciones'}
                  name={'description'}
                />
              </div>
              <div className={'mt-10 flex w-full justify-between'}>
                <ButtonCancelar
                  disabled={isSending}
                  onClick={() => {
                    setShowProductContainerModal(false)
                    form.reset()
                  }}
                  variant="outline"
                >
                  Cancelar
                </ButtonCancelar>
                {isSending ? (
                  <ButtonLoading
                    loading={isSending}
                    name="Guardando..."
                  ></ButtonLoading>
                ) : (
                  <ButtonPrimary
                    onClick={form.handleSubmit(saveProductContainer)}
                    text={'Guardar'}
                  />
                )}
              </div>
            </Form>
          </div>
        </Modal>
      )}
      */}

      {showBatchModal && (
        <Modal
          isOpen={showBatchModal}
          onClose={() => {
            setShowBatchModal(false)
            setIsBatchEditing(false)
            setBatchToEdit(null)
            setSelectedProductForBatch(null) // Clear the stored product selection
            form.reset()
          }}
          showDialogAlert={false}
          size={'xs'}
          title={
            isBatchEditing
              ? 'Editar Lote'
              : selectedProductForBatch
                ? `Agregar Lote - ${
                    typeof selectedProductForBatch === 'object'
                      ? selectedProductForBatch.title ||
                        selectedProductForBatch.name
                      : Array.isArray(selectedProductForBatch)
                        ? selectedProductForBatch[0]
                        : selectedProductForBatch
                  }`
                : 'Agregar Lote'
          }
        >
          <div
            className={
              'flex w-full flex-col overflow-y-scroll px-2 scrollbar-thin'
            }
          >
            <Form {...form}>
              <div className={'flex w-full flex-col space-y-2'}>
                <div className="flex w-full gap-2"></div>
                <div className="flex w-full flex-col">
                  <label className="mb-2 text-sm font-medium text-gray-700">
                    Fecha de expiración
                  </label>
                  <DateFilter
                    buttonClassName="w-full"
                    dateFrom={form.watch('expirationAt')}
                    dateTo={null}
                    hideDateTypeSelect
                    onDateRangeChange={(dateFrom) =>
                      form.setValue('expirationAt', dateFrom)
                    }
                    onDateTypeChange={() => {}}
                    onlySpecific
                    selectedDateType="specific"
                  />
                </div>
                <div className="flex w-full gap-2">
                  <div className="flex-1">
                    <InputForm
                      control={form.control}
                      label={'Cantidad'}
                      name={'entryQuantity'}
                      placeholder={'Cantidad de entrada'}
                      rules={{
                        required: 'Campo requerido',
                      }}
                      type="number"
                    />
                  </div>
                </div>
                <div className="flex w-full gap-2">
                  <div className="flex-1">
                    <InputForm
                      control={form.control}
                      label={'Número de paquetes (Opcional)'}
                      name={'noPackages'}
                      placeholder={'Número de paquetes'}
                      type="number"
                    />
                  </div>
                </div>
                <div className="flex w-full gap-2">
                  <div className="flex-1">
                    {providers && (
                      <SelectForm
                        control={form.control}
                        data={
                          Array.isArray(providers)
                            ? providers.map(({ id, name }) => ({
                                id,
                                title: name,
                              }))
                            : []
                        }
                        label={'Proveedor (*)'}
                        name={'supplierId'}
                      />
                    )}
                  </div>
                </div>
                <div className="flex w-full gap-2">
                  <div className="flex-1">
                    {currencies && (
                      <InputCurrencyForm
                        control={form.control}
                        currenciesData={
                          Array.isArray(currencies)
                            ? currencies.map(({ code }) => ({
                                code: code,
                                displayName: code,
                              }))
                            : []
                        }
                        label={'Precio registrado (*)'}
                        name={'registeredPrice'}
                        rules={{
                          required: 'Campo requerido',
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className={'mt-10 flex w-full justify-between'}>
                <ButtonCancelar
                  disabled={isSending}
                  onClick={() => {
                    setShowBatchModal(false)
                    setIsBatchEditing(false)
                    setBatchToEdit(null)
                    form.reset()
                  }}
                  variant="outline"
                >
                  Cancelar
                </ButtonCancelar>
                {isSending ? (
                  <ButtonLoading
                    loading={isSending}
                    name="Guardando..."
                  ></ButtonLoading>
                ) : (
                  <ButtonPrimary
                    onClick={form.handleSubmit(saveBatch)}
                    text={'Guardar'}
                  />
                )}
              </div>
            </Form>
          </div>
        </Modal>
      )}

      {showNewProductModal && (
        <Modal
          isOpen={showNewProductModal}
          onClose={() => {
            setIsCostEditing(false)
            setShowNewProductModal(false)
            setSelectedCategory(null)
            form.reset()
          }}
          showDialogAlert={false}
          size={'xs'}
          title={'Nuevo Producto'}
        >
          <div className={'flex w-full flex-col'}>
            <Form {...form}>
              <div className={'flex w-full flex-col space-y-2 p-1'} style={{ paddingBottom: '120px' }}>
                <SelectForm
                  control={form.control}
                  data={
                    Array.isArray(containerProductTypes)
                      ? containerProductTypes.map(({ id, name }) => ({
                          id,
                          title: name,
                        }))
                      : []
                  }
                  label={'Tipo (*)'}
                  name={'product_type'}
                  rules={{
                    required: 'Campo requerido',
                  }}
                  //defaultValue={selectedCategory}
                />
                <InputForm
                  control={form.control}
                  label={'Nombre'}
                  name={'product_name'}
                  placeholder={'Nombre'}
                  rules={{
                    required: 'Campo requerido',
                  }}
                />
                {form.watch('product_type') === 'STOCK' && currencies && (
                  <InputCurrencyForm
                    control={form.control}
                    currenciesData={
                      Array.isArray(currencies)
                        ? currencies.map(({ code }) => ({
                            code: code,
                            displayName: code,
                          }))
                        : []
                    }
                    label={'Precio de venta (*)'}
                    name={'product_price'}
                    rules={{
                      required: 'Campo requerido',
                    }}
                  />
                )}
              </div>
              <div className={'mt-10 flex w-full justify-between'}>
                <ButtonCancelar
                  disabled={isSending}
                  onClick={() => {
                    setShowNewProductModal(false)
                    form.reset()
                  }}
                  variant="outline"
                >
                  Cancelar
                </ButtonCancelar>
                {isSending ? (
                  <ButtonLoading
                    loading={isSending}
                    name="Guardando..."
                  ></ButtonLoading>
                ) : (
                  <ButtonPrimary
                    onClick={form.handleSubmit(saveNewProduct)}
                    text={'Guardar'}
                  />
                )}
              </div>
            </Form>
          </div>
        </Modal>
      )}

      {/* Modal de confirmación para eliminar producto */}
      {showDeleteProduct && (
        <Modal
          isOpen={showDeleteProduct}
          onClose={() => {
            setShowDeleteProduct(false)
            setProductToDelete(undefined)
          }}
          showDialogAlert={false}
          size={'xs'}
          title={'Eliminar Producto'}
        >
          <div className={'flex w-full flex-col space-y-4'}>
            <p>¿Está seguro que desea eliminar este producto?</p>
            <div className={'flex w-full justify-between'}>
              <ButtonCancelar
                onClick={() => {
                  setShowDeleteProduct(false)
                  setProductToDelete(undefined)
                }}
                variant="outline"
              >
                Cancelar
              </ButtonCancelar>
              <ButtonPrimary
                className="bg-red-600 hover:bg-red-700"
                onClick={() => {
                  if (productToDelete) {
                    deleteContainerProductsMutation.mutate(productToDelete)
                  }
                }}
                text={'Eliminar'}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de confirmación para eliminar lote */}
      {showDeleteBatch && (
        <Modal
          isOpen={showDeleteBatch}
          onClose={() => {
            setShowDeleteBatch(false)
            setBatchToDelete(undefined)
          }}
          showDialogAlert={false}
          size={'xs'}
          title={'Eliminar Lote'}
        >
          <div className={'flex w-full flex-col space-y-4'}>
            <p>¿Está seguro que desea eliminar este lote?</p>
            <div className={'flex w-full justify-between'}>
              <ButtonCancelar
                onClick={() => {
                  setShowDeleteBatch(false)
                  setBatchToDelete(undefined)
                }}
                variant="outline"
              >
                Cancelar
              </ButtonCancelar>
              <ButtonPrimary
                className="bg-red-600 hover:bg-red-700"
                onClick={() => {
                  if (batchToDelete) {
                    deleteContainerBatchMutation.mutate(batchToDelete)
                  }
                }}
                text={'Eliminar'}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
